import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Loader2 } from 'lucide-react';
import { ADMIN_BOUNTY_LOOKUP } from './bounty-shared';
import { MemberPicker, type PickedMember } from './MemberPicker';

type LookupRow = {
  reason: string | null;
  periodKey: string;
  deliveredAt: string | null;
  notifiedAt: string | null;
  emailedAt: string | null;
  completedAt: string | null;
  paidAt: string | null;
  pointsPaid: number;
  bounty: { id: string; name: string; slug: string; cadence: string; pointsReward: number };
};

function when(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

/**
 * ONE MEMBER, EVERY LIVE BOUNTY: whether it would pay them and why not, this period's
 * delivery stamps, and what it has paid them. Read-only on the server — looking at a
 * member never delivers or pays.
 */
export function BountyMemberLookup() {
  const [picked, setPicked] = useState<PickedMember | null>(null);
  const { data, loading } = useQuery<{ adminBountyLookup: LookupRow[] }>(ADMIN_BOUNTY_LOOKUP, {
    variables: { userId: picked?.asfId ?? '' },
    skip: !picked,
    fetchPolicy: 'network-only',
  });
  const rows = data?.adminBountyLookup ?? [];

  return (
    <div className="mb-8 rounded-lg border border-gray-200 bg-white p-4" data-testid="bounty-member-lookup">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm font-semibold text-gray-900">Look up a member</p>
        <MemberPicker picked={picked} onPick={setPicked} testId="bounty-lookup-search" />
      </div>
      {picked && loading ? <Loader2 className="mt-4 h-5 w-5 animate-spin text-gray-400" /> : null}
      {picked && !loading ? (
        rows.length === 0 ? (
          <p className="mt-4 text-sm text-gray-500">No live bounties.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-[760px] text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="py-2 pr-3">Bounty</th>
                  <th className="py-2 pr-3">Would pay?</th>
                  <th className="py-2 pr-3">Period</th>
                  <th className="py-2 pr-3">In inbox</th>
                  <th className="py-2 pr-3">Emailed</th>
                  <th className="py-2 pr-3">Paid</th>
                  <th className="py-2">Points paid</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.bounty.id} className="border-t">
                    <td className="py-2 pr-3 font-medium">{row.bounty.name}</td>
                    <td className={`py-2 pr-3 ${row.reason ? 'text-amber-700' : 'text-green-700'}`}>
                      {row.reason ?? 'Yes'}
                    </td>
                    <td className="py-2 pr-3 text-gray-600">{row.periodKey}</td>
                    <td className="py-2 pr-3 text-gray-600">{when(row.notifiedAt)}</td>
                    <td className="py-2 pr-3 text-gray-600">{when(row.emailedAt)}</td>
                    <td className="py-2 pr-3 text-gray-600">{when(row.paidAt)}</td>
                    <td className="py-2 text-gray-900">{row.pointsPaid}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </div>
  );
}
